import { MainHeading } from '@/components/common/MainHeading';
import { Layout } from '@/components/Layout/Layout'
import { PencilIcon, Plus } from 'lucide-react';
import { useEffect, useState, useCallback } from 'react'
import { IAddon, IAddonResponse } from '@/interfaces/Addon/addon.interface';
import CommonTable, { Action, TableColumn } from '@/components/common/CommonTable';
import { StatusBadge } from '@/components/common/StatusBadge';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { useNavigate } from 'react-router-dom';
import { useActiveRole } from '@/hooks/useActiveRole';
import { getRolePrefix, RoutesLink } from '@/utility/RouteLinks';
import { Switch } from '@/components/ui/switch';
import { useFilter } from '@/hooks/useFilter';
import { IFilter, IPageInfo } from '@/interfaces/common.interface';
import { formatRand, GetFilterUrl, showToastNotification } from '@/utility/helper';
import Pagination from '@/components/common/Pagination';
import { TableFilters } from '@/components/common/TableFilters';
import { TableItemIdentity } from '@/components/common/TableItemIdentity';
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';
import { UpdateAddonStockModal } from '@/components/common/Model/UpdateAddonStockModal';

export const AddOns = () => {
    const navigate = useNavigate();
    const { roleName } = useActiveRole();
    const prefix = getRolePrefix(roleName);
    const { canCreate, canEdit, hasPermission } = usePermissions();
    const canDo = canEdit(RESOURCES.ADDON);
    const canAddStock = hasPermission(RESOURCES.ADDON, 'add-stock');

    const [addons, setAddons] = useState<IAddon[]>([]);
    const [loading, setLoading] = useState(false);
    const [updatingId, setUpdatingId] = useState<string | null>(null);
    const [stockUpdateAddon, setStockUpdateAddon] = useState<IAddon | null>(null); 6
    const { filters, debouncedFilters, updateFilter, handlePageChange, handlePageSizeChange } = useFilter<IFilter>({
        search: '',
        page: 1,
        limit: 10,
    });

    const [pageInfo, setPageInfo] = useState<IPageInfo>({
        limit: 10,
        page: 1,
        total: 0,
        totalPages: 0,
    });

    useEffect(() => {
        fetchAddons();
    }, [debouncedFilters.search, debouncedFilters.page, debouncedFilters.limit]);

    const fetchAddons = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            const response = await callApi<IAddonResponse>(
                `${API_URL + API_ENDPOINT.addons}?${queryParams}`,
                { method: 'GET' }
            );
            setAddons(response?.data?.addons || []);
            setPageInfo(
                response?.data?.pageInfo || {
                    limit: 10, page: 1, total: 0,
                    totalPages: 0
                },
            );
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    }



    const handleStatusToggle = async (id: string, currentStatus: string) => {
        setUpdatingId(id);
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        try {
            const response = await callApi<any>(`${API_URL}${API_ENDPOINT.addons}/${id}`, {
                method: 'PATCH',
                data: { status: newStatus }
            });
            showToastNotification("success", response?.message || "Status updated successfully");

            // Update local state for immediate feedback
            setAddons(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
        } catch (error) {
            ThrowError(error);
        } finally {
            setUpdatingId(null);
        }
    };

    const handleStockUpdateSuccess = (addonId: string, newStock: number) => {
        setAddons(prev => prev.map(a => {
            if (a.id === addonId) {
                return {
                    ...a,
                    inventory: newStock,
                };
            }
            return a;
        }));
    };

    const columns: TableColumn<IAddon>[] = [
        {
            key: "product",
            header: "Linked Product",
            render: (item) => (
                <TableItemIdentity
                    image={item.product?.media?.[0]?.url}
                    name={item.product?.name || "N/A"}
                    subtitle={item.product?.category?.name || "N/A"}
                    fallbackText="No Img"
                />
            )
        },
        {
            key: "name",
            header: "Add-On",
            render: (item) => (
                <div>
                    {item.name}
                </div>
            )
        },
        {
            key: "price",
            header: "Price",
            render: (item) => <div className='font-bold text-primary'>
                {formatRand(item.price)}
            </div>
        },
        {
            key: "stockQuantity",
            header: "Stock",
            render: (item) => {
                const stock = item.inventory || 0;
                return (
                    <div className="flex flex-col">
                        <span className={`font-medium ${stock <= 5 ? 'text-destructive' : 'text-foreground'}`}>
                            {stock} Units
                        </span>
                        {stock <= 5 && (
                            <span className="text-[9px] text-destructive font-bold uppercase">Low Stock</span>
                        )}
                    </div>
                );
            }
        },
        {
            key: "status",
            header: "Status",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <Switch
                        checked={item.status === 'active'}
                        onCheckedChange={() => handleStatusToggle(item.id, item.status)}
                        disabled={updatingId === item.id || !canDo}
                        aria-label="Toggle status"
                    />
                    <StatusBadge status={item.status} />
                </div>
            )
        }
    ]

    const getActions = (_record: IAddon): Action<IAddon>[] => [
        ...(canDo ? [{
            text: "Edit",
            icon: <PencilIcon className='w-4 h-4 cursor-pointer' />,
            onClick: (record: IAddon) => {
                navigate(prefix + RoutesLink.EditAddOns.replace(':id', record.id));
            }
        }] : []),
        ...(canAddStock ? [{
            text: "Add Stock",
            icon: <Plus className='w-4 h-4 cursor-pointer' />,
            onClick: (record: IAddon) => {
                setStockUpdateAddon(record);
            }
        }] : [])
    ]

    return (
        <Layout>
            <MainHeading
                title="Add-Ons"
                description={`${pageInfo.total} Add-Ons Total · Extra Items Configuration`}
                actionLabel={canCreate(RESOURCES.ADDON) ? "Add Add-On" : ""}
                actionIcon={canCreate(RESOURCES.ADDON) ? <Plus className="w-4 h-4" /> : ""}
                onAction={() => {
                    navigate(prefix + RoutesLink.AddAddOns);
                }}
            />

            <div className="mt-6 flex flex-col gap-4 pb-12">
                <TableFilters
                    searchTerm={filters.search}
                    onSearchChange={(val) => updateFilter({ search: val })}
                    placeholder="Search add-ons..."
                />
                <CommonTable
                    columns={columns}
                    data={addons}
                    loading={loading}
                    emptyMessage="No Add-Ons found. Try adding one!"
                    actions={getActions}
                />

                <Pagination
                    currentPage={filters.page || 1}
                    totalPages={pageInfo.totalPages}
                    totalItems={pageInfo.total}
                    itemsPerPage={pageInfo.limit}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>

            <UpdateAddonStockModal
                isOpen={!!stockUpdateAddon}
                addon={stockUpdateAddon}
                onClose={() => setStockUpdateAddon(null)}
                onSuccess={handleStockUpdateSuccess}
            />
        </Layout>
    )
}
